/**
 * Video Helper Functions
 * Utility functions for video URL conversion and embedding
 * Supports both YouTube and Google Drive videos
 */

/**
 * Extract YouTube video ID from various URL formats
 * @param url - YouTube URL (watch?v=, youtu.be, or embed format)
 * @returns Video ID or null if invalid
 */
export function extractYouTubeVideoId(url: string): string | null {
  if (!url || typeof url !== 'string') {
    return null
  }

  // Handle different YouTube URL formats
  const patterns = [/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/, /^([a-zA-Z0-9_-]{11})$/]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }

  return null
}

/**
 * Convert YouTube URL to embed format
 * @param url - YouTube URL
 * @returns Embed URL or null if invalid
 */
export function getYouTubeEmbedUrl(url: string): string | null {
  const videoId = extractYouTubeVideoId(url)
  if (!videoId) {
    return null
  }

  return `https://www.youtube.com/embed/${videoId}`
}

/**
 * Check if URL is a Google Drive URL
 * @param url - URL to check
 * @returns true if Google Drive URL
 */
export function isGoogleDriveUrl(url: string): boolean {
  return /drive\.google\.com/.test(url)
}

/**
 * Extract Google Drive file ID from various URL formats
 * @param url - Google Drive URL
 * @returns File ID or null if invalid
 */
export function extractGoogleDriveFileId(url: string): string | null {
  if (!url || typeof url !== 'string') {
    return null
  }

  // Handle different Google Drive URL formats:
  // https://drive.google.com/file/d/FILE_ID/view?usp=sharing
  // https://drive.google.com/file/d/FILE_ID/view
  // https://drive.google.com/file/d/FILE_ID
  // https://drive.google.com/open?id=FILE_ID (alternative format)
  const patterns = [
    /\/d\/([a-zA-Z0-9_-]+)/, // Most common format: /d/FILE_ID
    /[?&]id=([a-zA-Z0-9_-]+)/ // Alternative format: ?id=FILE_ID or &id=FILE_ID
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }

  return null
}

/**
 * Convert Google Drive share URL to preview URL format
 * Example: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
 * Converts to: https://drive.google.com/file/d/FILE_ID/preview
 * @param url - Google Drive URL
 * @returns Preview URL or null if file ID cannot be extracted
 */
export function convertGoogleDriveUrl(url: string): string | null {
  const fileId = extractGoogleDriveFileId(url)
  if (!fileId) {
    return null
  }

  return `https://drive.google.com/file/d/${fileId}/preview`
}

/**
 * Get video embed URL based on video source type
 * Validates input and ensures proper URL format conversion
 * @param url - Video URL
 * @param videoSource - 'youtube' or 'google_drive'
 * @returns Embed URL or null if invalid or conversion fails
 * @throws Error if url or videoSource is invalid (for debugging purposes, though we return null in production)
 */
export function getVideoEmbedUrl(url: string, videoSource: 'youtube' | 'google_drive'): string | null {
  // Validate input
  if (!url || typeof url !== 'string' || !url.trim()) {
    return null
  }

  if (!videoSource || (videoSource !== 'youtube' && videoSource !== 'google_drive')) {
    return null
  }

  // Convert based on source type
  if (videoSource === 'youtube') {
    return getYouTubeEmbedUrl(url.trim())
  } else if (videoSource === 'google_drive') {
    return convertGoogleDriveUrl(url.trim())
  }

  return null
}
